import React, { Suspense } from 'react';
import {
  ThemeProvider,
  createTheme,
  StyledEngineProvider,
} from '@mui/material/styles';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MetaProvider } from './contexts/MetaContext';
import ProtectedRoute from './components/ProtectedRoute';
import Navigation from './components/Navigation';

// Lazy load components
const GroupPurchaseForm = React.lazy(() =>
  import('./components/GroupPurchaseForm')
);
const GroupPurchaseThankYou = React.lazy(() =>
  import('./components/GroupPurchaseThankYou')
);
const GroupPurchaseAdmin = React.lazy(() =>
  import('./components/GroupPurchaseAdmin')
);
const LeadCaptureForm = React.lazy(() =>
  import('./components/LeadCaptureForm')
);
const LeadTable = React.lazy(() => import('./components/LeadTable'));
const ThankYouPage = React.lazy(() => import('./components/ThankYouPage'));
const HomePage = React.lazy(() => import('./components/HomePage'));
const ProductManagement = React.lazy(() =>
  import('./components/ProductManagement')
);
const OrderPlacementWizard = React.lazy(() =>
  import('./components/OrderPlacementWizard')
);
const LoginScreen = React.lazy(() => import('./components/LoginScreen'));
const OrderThankYouPage = React.lazy(() =>
  import('./components/OrderThankYouPage')
);
const GeneratePDF = React.lazy(() => import('./components/GeneratePDF'));
const GenerateDeliveryNotePDF = React.lazy(() =>
  import('./components/GenerateDeliveryNotePDF')
);
const GenerateProductionPDF = React.lazy(() => import('./components/GenerateProductionPDF'));
const OrderManagement = React.lazy(() =>
  import('./components/OrderManagement')
);
const DataVisualizations = React.lazy(() =>
  import('./components/DataVisualizations')
);

// New content components
const ResourceManagement = React.lazy(() =>
  import('./components/ResourceManagement')
);
const KnowledgeGallery = React.lazy(() =>
  import('./components/KnowledgeGallery')
);
const PestsGallery = React.lazy(() =>
  import('./components/PestsGallery')
);
const ResourceDetail = React.lazy(() =>
  import('./components/ResourceDetail')
);
const ProductInfoPage = React.lazy(() =>
  import('./components/ProductInfoPage')
);
const ProductInfoManagement = React.lazy(() =>
  import('./components/ProductInfoManagement')
);
const OrderDiagnostic = React.lazy(() =>
  import('./components/OrderDiagnostic')
);
const InventoryManagement = React.lazy(() =>
  import('./components/InventoryManagement')
);
const PackagingItemManagement = React.lazy(() => import('./components/PackagingItemManagement'));
const ProductionOrderDashboard = React.lazy(() => import('./components/ProductionOrderDashboard'));
const ProductionOrderWizard = React.lazy(() => import('./components/ProductionOrderWizard'));
const ProductionSupplyDashboard = React.lazy(() => import('./components/ProductionSupplyDashboard'));

// Create rtl cache
const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

const theme = createTheme({
  direction: 'rtl',
  typography: {
    fontFamily: '"Rubik", Arial, sans-serif',
  },
  palette: {
    primary: {
      main: '#abcd37',
      light: '#7FC31',
      dark: '#f7ef1c',
      contrastText: '#fff',
    },
    secondary: {
      main: '#dc004e',
      light: '#e33371',
      dark: '#9a0036',
      contrastText: '#fff',
    },
  },
});

const CatalogRedirect = () => {
  window.location.href =
    'https://firebasestorage.googleapis.com/v0/b/rpc-leads.appspot.com/o/Catalog%20RPC%202027.pdf?alt=media&token=7b7030fa-59b7-4410-b0de-7cc1ac2cea3c';
  return null;
};

function AppContent() {
  const { user } = useAuth();
  const userRole = user?.role || localStorage.getItem('userRole');
  const isAdminOrSales = userRole === 'admin' || userRole === 'sales_manager';
  
  return (
    <>
      <Navigation userRole={userRole || 'guest'} />
      <Container maxWidth="xl">
        <Suspense fallback={<div>טוען...</div>}>
          <Routes>
            <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/form" element={<LeadCaptureForm />} />
          <Route
            path="/table"
            element={
              <ProtectedRoute allowedRoles={['sales_manager', 'admin']}>
                <LeadTable />
              </ProtectedRoute>
            }
          />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/welcome" element={<HomePage />} />
          <Route
            path="/products"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales_manager']}>
                <ProductManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/order"
            element={
              <ProtectedRoute
                allowedRoles={['client', 'sales_manager', 'admin']}
              >
                <OrderPlacementWizard />
              </ProtectedRoute>
            }
          />
          <Route path="/order-thank-you" element={<OrderThankYouPage />} />
          <Route
            path="/generate-pdf/:leadId/:orderId"
            element={<GeneratePDF />}
          />
          <Route
            path="/generate-delivery-note/:leadId/:orderId"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales_manager']}>
                <GenerateDeliveryNotePDF />
              </ProtectedRoute>
            }
          />
          <Route
            path="/generate-production-pdf/:orderId"
            element={<GenerateProductionPDF />}
          />
          <Route
            path="/sales"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales_manager']}>
                <OrderManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/data"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales_manager']}>
                <DataVisualizations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/order-diagnostic"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <OrderDiagnostic />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales_manager']}>
                <InventoryManagement />
              </ProtectedRoute>
            }
          />
          <Route path="/packaging-items" element={<ProtectedRoute allowedRoles={['admin', 'sales_manager', 'production_manager']}><PackagingItemManagement /></ProtectedRoute>} />
          <Route path="/production-orders/new" element={<ProtectedRoute allowedRoles={['admin', 'sales_manager']}><ProductionOrderWizard /></ProtectedRoute>} />
          <Route path="/production-orders" element={<ProtectedRoute allowedRoles={['admin', 'sales_manager', 'production_manager']}><ProductionOrderDashboard /></ProtectedRoute>} />
          <Route path="/production-supply" element={<ProtectedRoute allowedRoles={['admin', 'sales_manager']}><ProductionSupplyDashboard /></ProtectedRoute>} />
          <Route path="/catalog" element={<CatalogRedirect />} />
          <Route path="/group-purchase" element={<GroupPurchaseForm />} />
          <Route
            path="/group-purchase-thank-you"
            element={<GroupPurchaseThankYou />}
          />
          <Route
            path="/group-purchase-admin"
            element={
              <ProtectedRoute allowedRoles={['admin', 'sales_manager']}>
                <GroupPurchaseAdmin />
              </ProtectedRoute>
            }
          />
          
          {/* Content management routes */}
          <Route
            path="/content-management"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <ResourceManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/product-info-management"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <ProductInfoManagement />
              </ProtectedRoute>
            }
          />
          
          {/* Public content pages */}
          <Route path="/knowledge" element={<KnowledgeGallery />} />
          <Route path="/pests" element={<PestsGallery />} />
          <Route path="/product-info" element={<ProductInfoPage />} />
          <Route path="/:type/:id" element={<ResourceDetail />} />
        </Routes>
      </Suspense>
    </Container>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <StyledEngineProvider injectFirst>
        <CacheProvider value={cacheRtl}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <AuthProvider>
              <Router>
                <MetaProvider>
                  <AppContent />
                </MetaProvider>
              </Router>
            </AuthProvider>
          </ThemeProvider>
        </CacheProvider>
      </StyledEngineProvider>
    </HelmetProvider>
  );
}

export default App;
